<template>
  <component-wrapper class="product-category-overview-fi">
    <grid-container-fi v-if="fields && !isAppView">
      <div class="product-category-overview-fi__header fi-col-span-full">
        <!-- eslint-disable vue/no-v-html -->
        <span
          v-if="isEditMode"
          class="product-category-overview-fi__headline-small"
          v-html="fields.headlineSmall?.editable"
        />
        <span
          v-else-if="fields.headlineSmall"
          class="product-category-overview-fi__headline-small"
          v-html="fields.headlineSmall.value"
        />
        <h1
          v-if="isEditMode"
          class="product-category-overview-fi__headline fi-text-size-df-60"
          v-html="fields.headline?.editable"
        />
        <h1
          v-else-if="fields.headline"
          class="product-category-overview-fi__headline fi-text-size-df-60"
          v-html="fields.headline.value"
        />
        <!-- eslint-enable vue/no-v-html -->
      </div>
    </grid-container-fi>

    <section-divider-small-fi v-if="!isAppView" />

    <div class="product-category-overview-fi__container">
      <grid-container-fi>
        <div
          class="product-category-overview-fi__grid fi-col-start-1 fi-col-end-13 lg:fi-col-start-2 lg:fi-col-end-12"
        >
          <a
            v-for="item in getAllCategories"
            :id="item.CategoryId"
            :key="item.CategoryId"
            class="product-category-overview-fi__item"
            :href="productUrl(item.Slug)"
          >
            <div class="product-category-overview-fi__item-image">
              <cloud-image-fi
                :src="item.thumbnailUrl ? item.thumbnailUrl.Url : placeholder"
                :width="isMD ? '110' : '96'"
                :height="isMD ? '110' : '96'"
                params="func=fit"
                do-not-replace-url
                trim
                class="product-category-overview-fi__item-img"
                :alt="item.CategoryName"
              />
            </div>
            <span
              :lang="fischer.catalogLocale()"
              class="product-category-overview-fi__item-name"
            >
              {{ item.CategoryName }}
            </span>
          </a>
        </div>
      </grid-container-fi>
    </div>
  </component-wrapper>
</template>

<script setup lang="ts">
import { useQuery } from '@urql/vue';
import { CloudImageFi } from 'atoms';
import { computed, onMounted, onServerPrefetch } from 'vue';

import ComponentWrapper from '@/components/component-wrapper/ComponentWrapper.vue';
import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import { useAppEvent } from '@/composables/appEvent';
import { useBreakpoints } from '@/composables/breakpoint';
import { useFields } from '@/composables/fields';
import { useFischer } from '@/composables/fischerPlugin';
import { useSalesChannelFilter } from '@/composables/salesChannelFilter';
import type { GetAllCategoriesQuery, GetAllCategoriesQueryVariables } from '@/generated/graphql';
import GETALLCATEGORIES from '@/graphql/getAllCategories.gql';
import SectionDividerSmallFi from '@/pages/section-divider-fi/SectionDividerSmallFi.vue';

defineOptions({ name: 'ProductCategoryOverviewFi' });

const fields = useFields('ProductCategoryOverviewFiFields');

const fischer = useFischer();
const { isMD } = useBreakpoints();
const { getSalesChannels, isPublicAssortment } = useSalesChannelFilter();
const { sendAppEvent } = useAppEvent();

const placeholder = 'https://via.placeholder.com/110x110/CCCCCC/000000?text=Placeholder';

// R1: all-categories query — SEO-critical content, needs R0
const { data, executeQuery } = useQuery<GetAllCategoriesQuery, GetAllCategoriesQueryVariables>({
  query: GETALLCATEGORIES,
  variables: computed(() => ({
    baseSiteId: fischer.baseSiteId(),
    language: fischer.catalogLocale(),
    salesChannels: getSalesChannels(),
    publicAssortment: isPublicAssortment(),
  })),
});

// R0: category listing is SEO-critical, must be available on first SSR paint
onServerPrefetch(async () => {
  await executeQuery();
});

const getAllCategories = computed(
  () => data.value?.getAllCategories?.filter((category) => category !== null) ?? [],
);

const productUrl = (itemSlug: string) => `${fischer.pages.products().url}${itemSlug}`;

onMounted(() => {
  sendAppEvent('set-title', {
    title: fields.headline?.value,
    subtitle: fields.headlineSmall?.value,
  });
});
</script>

<style scoped src="./product-category-overview-fi" />
